import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, MailCheck, Power } from "lucide-react";
import { Button } from "@/components/ui/button";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface NoticeSettings {
  enabled: boolean;
  canEnable: boolean;
  testEmail: string | null;
  lastTestSentAt: string | null;
  lastTestSentTo: string | null;
}

async function readResponse<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => null);
  if (!response.ok) throw new Error(body?.error || `Request failed (HTTP ${response.status})`);
  return body as T;
}

export function BankDetailsNoticeControls({ isSuperAdmin }: { isSuperAdmin: boolean }) {
  const [settings, setSettings] = useState<NoticeSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<"test" | "toggle" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const refresh = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await fetch(`${BASE}/api/settings/bank-details-notice`, { credentials: "include" });
      setSettings(await readResponse<NoticeSettings>(response));
      if (!silent) setError(null);
    } catch (err) {
      if (!silent) setError(err instanceof Error ? err.message : "Unable to load notice settings");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(() => void refresh(true), 15_000);
    const onFocus = () => void refresh(true);
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [refresh]);

  async function sendTest() {
    if (!settings?.testEmail || busy) return;
    setBusy("test");
    setError(null);
    setMessage(null);
    try {
      const response = await fetch(`${BASE}/api/settings/bank-details-notice/test`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });
      const result = await readResponse<{ sentTo: string }>(response);
      setMessage(`Test sent to ${result.sentTo}. Check your inbox before turning on customer replies.`);
      await refresh(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Test email failed");
    } finally {
      setBusy(null);
    }
  }

  async function toggle() {
    if (!settings || busy || !isSuperAdmin) return;
    const enabled = !settings.enabled;
    if (enabled && !window.confirm(
      "Turn on live bank-details replies?\n\nNew, verified customer remittances will receive the bank-change email. Only one notice is sent per customer/address. This affects real customers.",
    )) return;
    setBusy("toggle");
    setError(null);
    setMessage(null);
    try {
      const response = await fetch(`${BASE}/api/settings/bank-details-notice`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled }),
      });
      const updated = await readResponse<NoticeSettings>(response);
      setSettings(updated);
      setMessage(enabled ? "Live customer replies are on." : "Live customer replies are off. A send already in progress may still finish.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to change notice setting");
      await refresh(true);
    } finally {
      setBusy(null);
    }
  }

  return (
    <section aria-labelledby="bank-notice-title" className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 id="bank-notice-title" className="text-lg font-semibold text-slate-900">Remittance bank-details reply</h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Thank customers for emailed remittances and send the Barclays bank-details notice once per verified customer.
            Only newly received remittances with a confirmed account and a registered sender address qualify.
          </p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-sm font-semibold ${settings?.enabled
          ? "border-emerald-200 bg-emerald-50 text-emerald-800"
          : "border-slate-200 bg-slate-50 text-slate-700"}`}>
          {loading && !settings ? "Loading…" : settings?.enabled ? "Live sending on" : "Live sending off"}
        </span>
      </div>

      {error && <div role="alert" className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</div>}
      {message && <div role="status" className="mt-4 flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />{message}
      </div>}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={sendTest} disabled={!settings?.testEmail || !!busy || loading}>
          {busy === "test" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <MailCheck className="mr-2 h-4 w-4" />}
          Send test to my email
        </Button>
        {isSuperAdmin && (
          <Button
            variant={settings?.enabled ? "outline" : "default"}
            onClick={toggle}
            disabled={!settings || !!busy || (!settings.enabled && !settings.canEnable)}
          >
            {busy === "toggle" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Power className="mr-2 h-4 w-4" />}
            {settings?.enabled ? "Turn off live replies" : "Turn on live replies"}
          </Button>
        )}
      </div>

      <div className="mt-3 space-y-1 text-xs text-slate-600">
        <p>Test recipient: <strong>{settings?.testEmail || "No email on your account"}</strong>. The test is clearly marked and does not email customers or use up a customer&apos;s one-time notice.</p>
        <p>The test checks the message and outgoing email only; it does not prove that an incoming customer remittance will qualify.</p>
        {settings?.lastTestSentAt && <p>Last successful test: {new Date(settings.lastTestSentAt).toLocaleString()} to {settings.lastTestSentTo}.</p>}
        {isSuperAdmin && !settings?.enabled && !settings?.canEnable && <p className="flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3.5 w-3.5" /> Send a successful test to your own email within 24 hours before turning on live replies.</p>}
        {!isSuperAdmin && <p>Only a super-admin can switch live customer replies on or off.</p>}
        <p>This setting applies to the current app environment. Switching off stops new sends; an email already in progress may still complete.</p>
      </div>
    </section>
  );
}